import { useEffect, useMemo, useState, useCallback } from 'react';
import { MIN_SENHA_LENGTH } from '@/lib/senha';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Mail, Building2, Clock, ShieldCheck, CalendarDays, CalendarRange,
  FolderKanban, Network, Workflow, Bell, Target, LayoutGrid, Download, IdCard, Eye, EyeOff, X, History,
  GraduationCap, ClipboardList,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { CargoGestorSelects } from '@/components/usuarios/CargoGestorSelects';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import {
  usuarioProfileService, ModuloResumo, UsuarioPerfilCompleto, AtividadeResponse,
  usuarioService, usuarioEmpresaService, departamentoService, empresaService, DiasTrabalhadosResposta,
} from '@/services/api';
import { sections, genericColumns, SectionConfig } from './usuarioDetalhes/sections';
import { saveOrShareBlob } from '@/lib/nativeFile';
import { buildUsuarioWorkbook } from '@/lib/usuarioExport';
import * as XLSX from 'xlsx';
import { fichaCadastralService, catalogoService } from '@/services/api';
import { Habilidade, CursoPessoal, UserRole, UsuarioEmpresa, Departamento, Empresa } from '@/types';
import { FichaCadastralForm, emptyFichaCadastral, fichaCadastralFromResponse } from '@/components/usuario/FichaCadastralForm';
import { AvatarUpload } from '@/components/usuario/AvatarUpload';
import { CalendarioAgenda } from './usuarioDetalhes/CalendarioAgenda';
import { AvisoSemRegistro, DiasTrabalhadosCard } from './usuarioDetalhes/DiasTrabalhadosCard';
import { ItensDoModulo } from './usuarioDetalhes/ItensDoModulo';
import { PERIODO_PRESETS, Periodo, PeriodoPreset, isPeriodoPreset, periodoDoPreset } from '@/lib/periodoFiltro';

async function exportCSV(nomeArquivo: string, config: SectionConfig | undefined, rows: Record<string, unknown>[]) {
  const cols = config?.columns ?? (rows[0] ? genericColumns(rows[0]) : []);
  const header = cols.map((c) => `"${c.label}"`).join(',');
  const body = rows.map((row) =>
    cols.map((c) => {
      const raw = row[c.key];
      const val = raw === null || raw === undefined ? '' : String(raw).replace(/"/g, '""');
      return `"${val}"`;
    }).join(',')
  );
  const csv = [header, ...body].join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  await saveOrShareBlob(blob, nomeArquivo);
}

const iconMap: Record<string, LucideIcon> = {
  ShieldCheck, CalendarDays, CalendarRange, FolderKanban, Network, Workflow, Bell, Target, Building2, History,
  GraduationCap, ClipboardList,
};

const formatDateTime = (iso: string | null) => {
  if (!iso) return 'Nunca acessou';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const roleLabels: Record<string, string> = {
  superadmin: 'Super Admin', admin: 'Administrador', comum: 'Comum',
};

const PAGE_SIZE = 20;

/** Módulos com visão própria dos itens da pessoa (backend: services/userProfile/itensDoUsuario.ts). */
const MODULOS_COM_ITENS = ['okrs', 'projetos', 'organogramas', 'fluxogramas', 'treinamentos', 'formularios'];

/** Módulos "de produto" mostrados em destaque na Visão geral, na mesma ordem do menu lateral
 *  (AppSidebar.tsx: OKRs, Projetos, Calendário, Organograma, Fluxogramas, Treinamentos,
 *  Formulários). Anexos em projetos ('cronogramas') fica fora por decisão da Samantha (28/09/2026). */
const MODULOS_PRINCIPAIS = ['okrs', 'projetos', 'calendario', 'organogramas', 'fluxogramas', 'treinamentos', 'formularios'];

export default function UsuarioDetalhes() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // `?modulo=` e `?periodo=` vêm do clique num número da tabela de Usuários > Dashboard.
  const [searchParams] = useSearchParams();
  const moduloInicial = searchParams.get('modulo');
  const periodoParam = searchParams.get('periodo');

  const [perfil, setPerfil] = useState<UsuarioPerfilCompleto | null>(null);
  const [resumo, setResumo] = useState<ModuloResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(moduloInicial || 'visao-geral');
  const [periodoPreset, setPeriodoPreset] = useState<PeriodoPreset>(isPeriodoPreset(periodoParam) ? periodoParam : 'tudo');
  const periodo = useMemo(() => periodoDoPreset(periodoPreset), [periodoPreset]);
  const [diasTrabalhados, setDiasTrabalhados] = useState<DiasTrabalhadosResposta | null>(null);

  // cache de atividade por módulo
  const [atividades, setAtividades] = useState<Record<string, AtividadeResponse>>({});
  const [loadingModulo, setLoadingModulo] = useState<string | null>(null);

  const [ficha, setFicha] = useState(emptyFichaCadastral);
  const [habilidadesCatalogo, setHabilidadesCatalogo] = useState<Habilidade[]>([]);
  const [cursosCatalogo, setCursosCatalogo] = useState<CursoPessoal[]>([]);
  const [loadingFicha, setLoadingFicha] = useState(true);
  const [salvandoFicha, setSalvandoFicha] = useState(false);
  const [empresaSelecionadaId, setEmpresaSelecionadaId] = useState('todas');
  // Escopo aplicado aos cards/módulos da Visão geral — 'todas' = sem filtro (undefined).
  const empresaFiltro = empresaSelecionadaId === 'todas' ? undefined : empresaSelecionadaId;
  const [outrosAbertos, setOutrosAbertos] = useState(false);

  // Dados da conta (antes era o dialog "Editar" na listagem de usuários)
  const isSuperAdmin = user?.tipo === 'superadmin';
  const isAdminOrSuperadmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const [accountForm, setAccountForm] = useState({
    nome: '', email: '', senha: '', tipo: 'comum' as UserRole, visualiza_apenas_proprio_dep: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);

  // Troca de senha própria (usuário comum vendo seu próprio perfil) — exige senha atual,
  // diferente do reset de senha de terceiro feito por admin logo acima (sem senha atual).
  const [passwordData, setPasswordData] = useState({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [usuarioEmpresas, setUsuarioEmpresas] = useState<UsuarioEmpresa[]>([]);
  const [isLoadingEmpresas, setIsLoadingEmpresas] = useState(false);
  const [todasEmpresas, setTodasEmpresas] = useState<Empresa[]>([]);
  const [departamentosPorEmpresa, setDepartamentosPorEmpresa] = useState<Record<string, Departamento[]>>({});
  const [novoAcesso, setNovoAcesso] = useState<{ empresa_id: string; tipo: 'admin' | 'comum'; departamento_id: string }>({
    empresa_id: '', tipo: 'comum', departamento_id: '',
  });
  const [exportingTudo, setExportingTudo] = useState(false);

  useEffect(() => {
    if (user && user.tipo !== 'superadmin' && user.tipo !== 'admin' && user.id !== id) {
      navigate('/usuarios', { replace: true });
    }
  }, [user, id, navigate]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      usuarioProfileService.getPerfil(id),
      // "acessos" é a lista de vínculos por empresa (alimenta o próprio seletor) —
      // não é escopada pelo filtro, carregada já de cara pra saber "quantas empresas".
      usuarioProfileService.getAtividade(id, 'acessos', { page: 1, limit: 50 }),
    ]).then(([p, acessos]) => {
      setPerfil(p);
      setAtividades((prev) => ({ ...prev, acessos }));
      setLoading(false);
    });
  }, [id]);

  // Cards da Visão geral — recarregam quando a empresa ou o período selecionados no
  // header do perfil mudam. O cache dos módulos é limpo e o efeito abaixo recarrega o
  // módulo aberto (se houver) com o filtro novo.
  useEffect(() => {
    if (!id) return;
    usuarioProfileService.getResumo(id, empresaFiltro, periodo).then(setResumo);
    setAtividades((prev) => ({ acessos: prev.acessos }));
  }, [id, empresaFiltro, periodo]);

  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    usuarioProfileService.getDiasTrabalhados(id, periodo).then((r) => { if (!cancelado) setDiasTrabalhados(r); });
    return () => { cancelado = true; };
  }, [id, periodo]);

  const abaEhModulo = tab !== 'visao-geral' && tab !== 'ficha-cadastral' && tab !== 'acesso-empresas';
  useEffect(() => {
    if (!id || !abaEhModulo || atividades[tab]) return;
    let cancelado = false;
    setLoadingModulo(tab);
    usuarioProfileService
      .getAtividade(id, tab, { page: 1, limit: PAGE_SIZE, empresaId: empresaFiltro, ...periodo })
      .then((res) => {
        if (cancelado) return;
        setAtividades((prev) => ({ ...prev, [tab]: res }));
        setLoadingModulo(null);
      });
    return () => { cancelado = true; };
  }, [id, tab, abaEhModulo, atividades, empresaFiltro, periodo]);

  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    setLoadingFicha(true);
    setFicha(emptyFichaCadastral);
    Promise.all([
      fichaCadastralService.get(id),
      catalogoService.listHabilidades(),
      catalogoService.listCursosPessoais(),
    ]).then(([fichaResponse, habilidadesResponse, cursosResponse]) => {
      if (cancelado) return;
      setFicha(fichaResponse.success ? fichaCadastralFromResponse(fichaResponse.data) : emptyFichaCadastral);
      if (habilidadesResponse.success && habilidadesResponse.data) setHabilidadesCatalogo(habilidadesResponse.data);
      if (cursosResponse.success && cursosResponse.data) setCursosCatalogo(cursosResponse.data);
      setLoadingFicha(false);
    });
    return () => {
      cancelado = true;
    };
  }, [id]);

  useEffect(() => {
    if (!perfil) return;
    setAccountForm({
      nome: perfil.usuario.nome,
      email: perfil.usuario.email,
      senha: '',
      tipo: perfil.usuario.tipo,
      visualiza_apenas_proprio_dep: perfil.usuario.visualiza_apenas_proprio_dep ?? false,
    });
  }, [perfil]);

  const loadDepartamentosForEmpresa = useCallback(async (empresaId: string) => {
    if (!empresaId || departamentosPorEmpresa[empresaId]) return;
    const res = await departamentoService.getByEmpresa(empresaId);
    if (res.success) {
      setDepartamentosPorEmpresa((prev) => ({ ...prev, [empresaId]: res.data }));
    }
  }, [departamentosPorEmpresa]);

  const loadUsuarioEmpresas = useCallback(async (usuarioId: string) => {
    setIsLoadingEmpresas(true);
    const res = await usuarioEmpresaService.getByUsuario(usuarioId);
    if (res.success) {
      setUsuarioEmpresas(res.data);
      res.data.forEach((ue) => loadDepartamentosForEmpresa(ue.empresa_id));
    }
    setIsLoadingEmpresas(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // /usuarios-empresas é admin-only no backend — comum vendo o próprio perfil não chama.
    if (!id || !isAdminOrSuperadmin) return;
    loadUsuarioEmpresas(id);
  }, [id, isAdminOrSuperadmin, loadUsuarioEmpresas]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    empresaService.getAll().then((res) => {
      if (res.success) setTodasEmpresas(res.data.filter((e) => e.status === 'ativa'));
    });
  }, [isSuperAdmin]);

  useEffect(() => {
    if (novoAcesso.empresa_id) loadDepartamentosForEmpresa(novoAcesso.empresa_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novoAcesso.empresa_id]);

  const handleSaveAccount = async () => {
    if (!id) return;
    setSavingAccount(true);
    const updateData: Partial<{ nome: string; email: string; senha: string; tipo: UserRole; visualiza_apenas_proprio_dep: boolean }> = {
      nome: accountForm.nome,
      email: accountForm.email,
      tipo: accountForm.tipo,
      visualiza_apenas_proprio_dep: accountForm.tipo === 'comum' ? accountForm.visualiza_apenas_proprio_dep : false,
    };
    if (accountForm.senha) updateData.senha = accountForm.senha;

    const result = await usuarioService.update(id, updateData);
    if (result.success) {
      toast.success('Dados da conta atualizados');
      setAccountForm((prev) => ({ ...prev, senha: '' }));
      setPerfil((p) => (p ? { ...p, usuario: { ...p.usuario, ...updateData } } : p));
    } else {
      toast.error(result.error || 'Erro ao atualizar dados da conta');
    }
    setSavingAccount(false);
  };

  const handleAdicionarAcesso = async () => {
    if (!id) return;
    if (!novoAcesso.empresa_id) {
      toast.error('Selecione uma empresa');
      return;
    }
    const res = await usuarioEmpresaService.create({
      usuario_id: id,
      empresa_id: novoAcesso.empresa_id,
      tipo: novoAcesso.tipo,
      departamento_id: novoAcesso.departamento_id || null,
    });
    if (res.success) {
      toast.success('Acesso adicionado');
      setNovoAcesso({ empresa_id: '', tipo: 'comum', departamento_id: '' });
      loadUsuarioEmpresas(id);
    } else {
      toast.error(res.error || 'Erro ao adicionar acesso');
    }
  };

  const handleAtualizarAcesso = async (
    ueId: string,
    data: { tipo?: 'admin' | 'comum'; departamento_id?: string | null; cargo_id?: string | null; gestor_usuario_id?: string | null },
  ) => {
    if (!id) return;
    const res = await usuarioEmpresaService.update(ueId, data);
    if (res.success) {
      toast.success('Acesso atualizado');
      loadUsuarioEmpresas(id);
    } else {
      toast.error(res.error || 'Erro ao atualizar acesso');
    }
  };

  const handleRemoverAcesso = async (ueId: string) => {
    if (!id) return;
    const res = await usuarioEmpresaService.remove(ueId);
    if (res.success) {
      toast.success('Acesso removido');
      loadUsuarioEmpresas(id);
    } else {
      toast.error(res.error || 'Erro ao remover acesso');
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePasswordForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!passwordData.senhaAtual) errors.senhaAtual = 'Senha atual é obrigatória';
    if (!passwordData.novaSenha) errors.novaSenha = 'Nova senha é obrigatória';
    else if (passwordData.novaSenha.length < MIN_SENHA_LENGTH) errors.novaSenha = `Senha deve ter no mínimo ${MIN_SENHA_LENGTH} caracteres`;
    if (!passwordData.confirmarSenha) errors.confirmarSenha = 'Confirmação é obrigatória';
    if (passwordData.novaSenha && passwordData.confirmarSenha && passwordData.novaSenha !== passwordData.confirmarSenha) {
      errors.confirmarSenha = 'As senhas não conferem';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;
    setUpdatingPassword(true);
    const response = await usuarioService.updatePassword(
      passwordData.senhaAtual, passwordData.novaSenha, passwordData.confirmarSenha
    );
    if (response.success) {
      toast.success('Senha alterada com sucesso!');
      setPasswordData({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
      setPasswordErrors({});
    } else {
      toast.error(response.error || 'Erro ao alterar senha');
    }
    setUpdatingPassword(false);
  };

  const handleSalvarFicha = async () => {
    if (!id) return;
    setSalvandoFicha(true);
    const response = await fichaCadastralService.update(id, ficha);
    if (response.success && response.data) {
      setFicha(fichaCadastralFromResponse(response.data));
      toast.success('Ficha cadastral salva com sucesso!');
    } else {
      toast.error(response.error || 'Erro ao salvar ficha cadastral');
    }
    setSalvandoFicha(false);
  };

  // Módulos com conteúdo navegável a partir do card da Visão geral (o backend já
  // não retorna 'auditoria' pra quem não pode ver — não precisa filtrar de novo aqui).
  // Não viram aba fixa: só existe TabsContent pra cada um, acessado pelo clique no card.
  const modulos = resumo;
  const modulosPrincipais = MODULOS_PRINCIPAIS
    .map((key) => modulos.find((m) => m.key === key))
    .filter((m): m is ModuloResumo => Boolean(m));
  const modulosOutros = modulos
    .filter((m) => m.key !== 'acessos' && m.key !== 'cronogramas' && !MODULOS_PRINCIPAIS.includes(m.key))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

  const empresasVinculadas = useMemo(() => {
    const rows = atividades.acessos?.data ?? [];
    return rows.map((r) => ({
      id: String(r.id ?? ''),
      empresa: (r.empresa as string | null) ?? 'Empresa removida',
      departamento: (r.departamento as string | null) ?? null,
      tipo: (r.tipo as string) ?? 'comum',
      ativo: Boolean(r.ativo),
    }));
  }, [atividades.acessos]);

  const empresaSelecionada =
    empresaSelecionadaId === 'todas'
      ? null
      : empresasVinculadas.find((e) => e.id === empresaSelecionadaId) ?? null;

  const handleExportarTudo = async () => {
    if (!id || !perfil) return;
    setExportingTudo(true);
    try {
      const workbook = await buildUsuarioWorkbook(id, empresaFiltro);
      if (!workbook) throw new Error('Não foi possível montar a exportação');
      const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      await saveOrShareBlob(blob, `usuario_${u.nome.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Exportação concluída');
    } catch {
      toast.error('Erro ao exportar dados do usuário');
    } finally {
      setExportingTudo(false);
    }
  };

  const handleTab = (value: string) => {
    setTab(value);
  };

  /** Card de resumo por módulo na Visão geral. `accent` diferencia módulo de produto (dourado) de outros indicadores (neutro). */
  const renderResumoCard = (m: ModuloResumo, accent: boolean) => {
    const Icon = iconMap[m.icon] ?? LayoutGrid;
    return (
      <Card
        key={m.key}
        className="cursor-pointer transition-colors hover:bg-muted/50"
        onClick={() => handleTab(m.key)}
      >
        <CardContent className="p-5">
          <div
            className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${
              accent ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
            }`}
          >
            <Icon className="h-[18px] w-[18px]" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">{m.label}</p>
          <div className="mt-1.5 text-2xl font-bold">{m.total}</div>
          {m.indireto && (
            <p className="mt-1 text-[11px] text-muted-foreground">vínculo indireto</p>
          )}
          {Object.entries(m.extra).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {Object.entries(m.extra).map(([k, v]) => (
                <span key={k} className="text-[11px] text-muted-foreground">
                  {k.replace(/_/g, ' ')}: {v}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!perfil) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/usuarios')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
          </Button>
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              Usuário não encontrado.
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const u = perfil.usuario;

  return (
    <AppLayout>
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(isAdminOrSuperadmin ? '/usuarios' : '/')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>

        {/* Header do usuário — avatar compartilhado entre as duas linhas (identidade + ações) */}
        <Card className="overflow-hidden">
          <CardContent className="flex items-stretch gap-0 p-0">
            <div className="flex items-center justify-center p-4 pr-0">
              <AvatarUpload
                usuarioId={u.id}
                nome={u.nome}
                avatarUrl={u.avatar_url}
                onChange={(avatar_url) =>
                  setPerfil((p) => (p ? { ...p, usuario: { ...p.usuario, avatar_url } } : p))
                }
                size="lg"
              />
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-3 p-4">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h1 className="text-base font-bold leading-none">{u.nome}</h1>
                  <Badge variant="secondary">{roleLabels[u.tipo] ?? u.tipo}</Badge>
                  {empresasVinculadas.length > 1 && (
                    <Badge variant="outline" className="gap-1 text-xs">
                      <Building2 className="h-3 w-3" /> {empresasVinculadas.length} empresas
                    </Badge>
                  )}
                </div>
                <span className="text-muted-foreground">·</span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Mail className="h-4 w-4 shrink-0" /> {u.email}
                </span>
                {u.empresa?.nome && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Building2 className="h-4 w-4 shrink-0" /> {u.empresa.nome}
                    </span>
                  </>
                )}
                <span className="text-muted-foreground">·</span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 shrink-0" /> Último acesso: {formatDateTime(perfil.ultimo_acesso)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {id && <AvisoSemRegistro resposta={diasTrabalhados} userId={id} />}

        <Tabs value={tab} onValueChange={handleTab}>
          <TabsList>
            <TabsTrigger value="visao-geral" className="gap-1.5">
              <LayoutGrid className="h-4 w-4" /> Visão geral
            </TabsTrigger>
            <TabsTrigger value="ficha-cadastral" className="gap-1.5">
              <IdCard className="h-4 w-4" /> Ficha Cadastral
            </TabsTrigger>
            {isAdminOrSuperadmin && (
              <TabsTrigger value="acesso-empresas" className="gap-1.5">
                <Building2 className="h-4 w-4" /> Acesso a empresas
              </TabsTrigger>
            )}
          </TabsList>

          {/* Visão geral: cards de contagem por módulo, em 2 grupos (módulos de produto x outros indicadores) */}
          <TabsContent value="visao-geral" className="mt-4 space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Módulos
              </p>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {modulosPrincipais.map((m) => renderResumoCard(m, true))}
              </div>
            </div>
            <div>
              <button
                type="button"
                onClick={() => setOutrosAbertos((v) => !v)}
                className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
              >
                Outros indicadores {outrosAbertos ? '▾' : '...'}
              </button>
              {outrosAbertos && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  {modulosOutros.map((m) => renderResumoCard(m, false))}
                </div>
              )}
            </div>
            {id && <DiasTrabalhadosCard userId={id} dataInicial={periodo.ate} />}
          </TabsContent>

          {/* Ficha cadastral do usuário */}
          <TabsContent value="ficha-cadastral" className="mt-4 space-y-4">
            {!isAdminOrSuperadmin ? (
              <>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ShieldCheck className="h-4 w-4 text-muted-foreground" /> Dados da conta
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Nome</Label>
                        <p className="text-sm font-medium">{u.nome}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Email</Label>
                        <p className="text-sm font-medium">{u.email}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Tipo de usuário</Label>
                      <div className="mt-1">
                        <Badge variant="secondary">{roleLabels[u.tipo] ?? u.tipo}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Alterar senha</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleUpdatePassword} className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor="senha-atual">Senha atual</Label>
                          <div className="relative">
                            <Input
                              id="senha-atual"
                              type={showPasswords.current ? 'text' : 'password'}
                              value={passwordData.senhaAtual}
                              onChange={(e) => {
                                setPasswordData((p) => ({ ...p, senhaAtual: e.target.value }));
                                if (passwordErrors.senhaAtual) setPasswordErrors((p) => ({ ...p, senhaAtual: '' }));
                              }}
                              className={`pr-10 ${passwordErrors.senhaAtual ? 'border-destructive' : ''}`}
                            />
                            <button
                              type="button"
                              onClick={() => togglePasswordVisibility('current')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              tabIndex={-1}
                            >
                              {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {passwordErrors.senhaAtual && (
                            <p className="text-xs text-destructive">{passwordErrors.senhaAtual}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="senha-nova">Nova senha</Label>
                          <div className="relative">
                            <Input
                              id="senha-nova"
                              type={showPasswords.new ? 'text' : 'password'}
                              value={passwordData.novaSenha}
                              onChange={(e) => {
                                setPasswordData((p) => ({ ...p, novaSenha: e.target.value }));
                                if (passwordErrors.novaSenha) setPasswordErrors((p) => ({ ...p, novaSenha: '' }));
                              }}
                              className={`pr-10 ${passwordErrors.novaSenha ? 'border-destructive' : ''}`}
                            />
                            <button
                              type="button"
                              onClick={() => togglePasswordVisibility('new')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              tabIndex={-1}
                            >
                              {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {passwordErrors.novaSenha && (
                            <p className="text-xs text-destructive">{passwordErrors.novaSenha}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="senha-confirma">Confirmar nova senha</Label>
                          <div className="relative">
                            <Input
                              id="senha-confirma"
                              type={showPasswords.confirm ? 'text' : 'password'}
                              value={passwordData.confirmarSenha}
                              onChange={(e) => {
                                setPasswordData((p) => ({ ...p, confirmarSenha: e.target.value }));
                                if (passwordErrors.confirmarSenha) setPasswordErrors((p) => ({ ...p, confirmarSenha: '' }));
                              }}
                              className={`pr-10 ${passwordErrors.confirmarSenha ? 'border-destructive' : ''}`}
                            />
                            <button
                              type="button"
                              onClick={() => togglePasswordVisibility('confirm')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              tabIndex={-1}
                            >
                              {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {passwordErrors.confirmarSenha && (
                            <p className="text-xs text-destructive">{passwordErrors.confirmarSenha}</p>
                          )}
                        </div>
                      </div>
                      <Button type="submit" disabled={updatingPassword}>
                        {updatingPassword ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Alterando...
                          </>
                        ) : (
                          'Alterar senha'
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </>
            ) : (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" /> Dados da conta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="conta-nome">Nome</Label>
                    <Input
                      id="conta-nome"
                      value={accountForm.nome}
                      onChange={(e) => setAccountForm({ ...accountForm, nome: e.target.value })}
                      placeholder="Nome completo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="conta-email">Email</Label>
                    <Input
                      id="conta-email"
                      type="email"
                      value={accountForm.email}
                      onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                      placeholder="email@empresa.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="conta-senha">Nova senha (deixe em branco para manter)</Label>
                    <div className="relative">
                      <Input
                        id="conta-senha"
                        type={showPassword ? 'text' : 'password'}
                        value={accountForm.senha}
                        onChange={(e) => setAccountForm({ ...accountForm, senha: e.target.value })}
                        placeholder="••••••"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="conta-tipo">Tipo de usuário</Label>
                    <Select
                      value={accountForm.tipo}
                      onValueChange={(v) => setAccountForm({ ...accountForm, tipo: v as UserRole })}
                      disabled={accountForm.tipo === 'superadmin'}
                    >
                      <SelectTrigger id="conta-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="comum">Usuário Comum</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                        {accountForm.tipo === 'superadmin' && (
                          <SelectItem value="superadmin">Super Admin</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {accountForm.tipo === 'comum' && (
                  <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <Label>Só visualiza OKRs do próprio departamento</Label>
                      <p className="text-xs text-muted-foreground">
                        Quando ativo, o usuário verá apenas OKRs do departamento vinculado
                      </p>
                    </div>
                    <Switch
                      checked={accountForm.visualiza_apenas_proprio_dep}
                      onCheckedChange={(v) => setAccountForm({ ...accountForm, visualiza_apenas_proprio_dep: v })}
                    />
                  </div>
                )}

                <Button onClick={handleSaveAccount} disabled={savingAccount}>
                  {savingAccount ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar dados da conta'
                  )}
                </Button>
              </CardContent>
            </Card>
            )}

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <IdCard className="h-4 w-4 text-muted-foreground" /> Dados cadastrais
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingFicha ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <FichaCadastralForm
                      values={ficha}
                      onChange={(patch) => setFicha((prev) => ({ ...prev, ...patch }))}
                      habilidadesCatalogo={habilidadesCatalogo}
                      cursosCatalogo={cursosCatalogo}
                      idPrefix="usuario-detalhes-ficha"
                    />
                    <Button onClick={handleSalvarFicha} disabled={salvandoFicha}>
                      {salvandoFicha ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        'Salvar ficha cadastral'
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Acesso a empresas: era uma seção dentro de Ficha Cadastral, virou aba própria */}
          {isAdminOrSuperadmin && (
            <TabsContent value="acesso-empresas" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="h-4 w-4 text-muted-foreground" /> Acesso a empresas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isLoadingEmpresas ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" /> Carregando...
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {usuarioEmpresas.map((ue) => {
                        const deps = departamentosPorEmpresa[ue.empresa_id] ?? [];
                        return (
                          <div key={ue.id} className="space-y-2 rounded border px-3 py-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{ue.empresa?.nome ?? ue.empresa_id}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleRemoverAcesso(ue.id)}
                              >
                                <X className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </div>
                            <div className="flex gap-2">
                              <Select
                                value={ue.tipo}
                                onValueChange={(v) => handleAtualizarAcesso(ue.id, { tipo: v as 'admin' | 'comum' })}
                              >
                                <SelectTrigger className="h-7 w-28 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="comum">Usuário</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select
                                value={ue.departamento_id ?? ''}
                                onValueChange={(v) => handleAtualizarAcesso(ue.id, { departamento_id: v || null })}
                              >
                                <SelectTrigger className="h-7 flex-1 text-xs">
                                  <SelectValue placeholder="Departamento" />
                                </SelectTrigger>
                                <SelectContent>
                                  {deps.map((dep) => (
                                    <SelectItem key={dep.id} value={dep.id}>{dep.nome}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <CargoGestorSelects
                              compacto
                              empresaId={ue.empresa_id}
                              usuarioId={id}
                              cargoId={ue.cargo_id ?? null}
                              gestorId={ue.gestor_usuario_id ?? null}
                              gestorNome={ue.gestor?.nome}
                              onChange={(campo, valor) => handleAtualizarAcesso(ue.id, { [campo]: valor })}
                            />
                          </div>
                        );
                      })}

                      {isSuperAdmin && (
                        <div className="space-y-2 pt-1">
                          <div className="flex gap-2">
                            <Select
                              value={novoAcesso.empresa_id}
                              onValueChange={(v) => setNovoAcesso({ ...novoAcesso, empresa_id: v, departamento_id: '' })}
                            >
                              <SelectTrigger className="h-8 flex-1 text-sm">
                                <SelectValue placeholder="Selecionar empresa" />
                              </SelectTrigger>
                              <SelectContent>
                                {todasEmpresas
                                  .filter((e) => !usuarioEmpresas.some((ue) => ue.empresa_id === e.id))
                                  .map((e) => (
                                    <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={novoAcesso.tipo}
                              onValueChange={(v) => setNovoAcesso({ ...novoAcesso, tipo: v as 'admin' | 'comum' })}
                            >
                              <SelectTrigger className="h-8 w-24 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="comum">Usuário</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {novoAcesso.empresa_id && (
                            <Select
                              value={novoAcesso.departamento_id}
                              onValueChange={(v) => setNovoAcesso({ ...novoAcesso, departamento_id: v })}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Departamento (opcional)" />
                              </SelectTrigger>
                              <SelectContent>
                                {(departamentosPorEmpresa[novoAcesso.empresa_id] ?? []).map((dep) => (
                                  <SelectItem key={dep.id} value={dep.id}>{dep.nome}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <Button type="button" variant="outline" size="sm" onClick={handleAdicionarAcesso}>
                            Adicionar acesso
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Uma TabsContent por módulo — só acessível pelo clique no card da Visão geral */}
          {modulos.filter((m) => m.key !== 'acessos').map((m) => (
            <TabsContent key={m.key} value={m.key} className="mt-4 space-y-3">
              <Button variant="ghost" size="sm" className="-ml-2" onClick={() => handleTab('visao-geral')}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Voltar à visão geral
              </Button>
              {MODULOS_COM_ITENS.includes(m.key) ? (
                // Itens da pessoa (visual do módulo) + histórico de ações numa aba ao lado
                <Tabs defaultValue="itens" className="space-y-3">
                  <TabsList>
                    <TabsTrigger value="itens">Itens</TabsTrigger>
                    <TabsTrigger value="historico">Histórico de ações</TabsTrigger>
                  </TabsList>
                  <TabsContent value="itens" className="mt-0">
                    <ItensDoModulo userId={id!} modulo={m.key} label={m.label} empresaId={empresaFiltro} />
                  </TabsContent>
                  <TabsContent value="historico" className="mt-0">
                    <ModuloTabela
                      userId={id!}
                      modulo={m.key}
                      label={m.label}
                      loading={loadingModulo === m.key}
                      resposta={atividades[m.key]}
                      empresaId={empresaFiltro}
                      periodo={periodo}
                    />
                  </TabsContent>
                </Tabs>
              ) : (
                <ModuloTabela
                  userId={id!}
                  modulo={m.key}
                  label={m.label}
                  loading={loadingModulo === m.key}
                  resposta={atividades[m.key]}
                  empresaId={empresaFiltro}
                  periodo={periodo}
                />
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AppLayout>
  );
}

function ModuloTabela({
  userId, modulo, label, loading, resposta, empresaId, periodo,
}: {
  userId: string;
  modulo: string;
  label: string;
  loading: boolean;
  resposta?: AtividadeResponse;
  empresaId?: string;
  periodo: Periodo;
}) {
  const [exporting, setExporting] = useState(false);
  const config = sections[modulo];
  const rows = resposta?.data ?? [];
  const columns = config?.columns ?? (rows[0] ? genericColumns(rows[0]) : []);

  const handleExport = async () => {
    setExporting(true);
    try {
      let allRows = rows;
      if (!resposta || resposta.total > rows.length) {
        const res = await usuarioProfileService.getAtividade(userId, modulo, { page: 1, limit: 9999, empresaId, ...periodo });
        allRows = res.data;
      }
      await exportCSV(`${modulo}-${userId}.csv`, config, allRows);
    } finally {
      setExporting(false);
    }
  };

  if (loading || !resposta) {
    return (
      <Card>
        <CardContent className="py-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const ehAgenda = modulo === 'calendario';

  if (rows.length === 0 && !ehAgenda) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {config?.emptyLabel ?? 'Nenhum registro encontrado.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium">{label}</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{resposta.total} no total</Badge>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
            {exporting
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Download className="h-4 w-4" />}
            <span className="ml-1.5">Exportar CSV</span>
          </Button>
        </div>
      </CardHeader>
      {ehAgenda ? (
        <CardContent>
          <CalendarioAgenda userId={userId} empresaId={empresaId} dataInicial={periodo.de} />
        </CardContent>
      ) : (
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead key={c.key}>{c.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={(row.id as string) ?? i}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className="text-sm">
                      {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {resposta.total > rows.length && (
          <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
            Exibindo {rows.length} de {resposta.total} registros. O CSV exporta todos.
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
}
